import * as React from 'react';
import { Link } from 'react-router-dom';

import ActionButton from '../containers/ActionButton';
import SSOAliasField from '../containers/SSOAliasField';
import EmailField from '../containers/EmailField';
import Footer from './Footer';
import Notification from '../containers/Notification';

import logo from '../images/blt-logo-web.svg';
import * as styles from './LoginSso.module.scss';
import l10n from '@built/i18n';

/**
 * Renders the SSO-specific login view; this will collect a user's
 * email address and look up whether they should be redirected
 * to an SSO endpoint. If we receive a redirect URL, then we
 * issue a browser redirect to initiate SSO
 */
export default function LoginSso(props: LoginSsoProps) {
  const builtLogo = logo;

  const renderDangerNotification = (
    title: string,
    body: string
  ): React.ReactNode => {
    return (
      <Notification type="danger" title={title} className={styles.errorMessage}>
        <p>{body}</p>
      </Notification>
    );
  };

  return (
    <div className={styles.componentContainer}>
      <div className={styles.loginFormContainer}>
        <form className={styles.loginForm} onSubmit={props.onSubmit}>
          <Link
            to="/"
            className={styles.backLink}
            onClick={props.onBackButtonClick}
          >
            &larr;{' '}
            {l10n.t('LoginSso:Link.backStandard', 'Back to Standard Login')}
          </Link>
          <img className={styles.logo} src={builtLogo} alt="Built Logo" />

          {props.showSsoError && props.alias !== '' && (
            <>
              {renderDangerNotification(
                l10n.t(
                  'LoginSso:Notification.Alias.notConfigured.title',
                  'Invalid Organization ID'
                ),
                l10n.t(
                  'LoginSso:Notification.Alias.notConfigured.message',
                  `Please contact your IT administrator for more information.`
                )
              )}
            </>
          )}

          {props.showSsoError && props.email !== '' && (
            <>
              {renderDangerNotification(
                l10n.t(
                  'LoginSso:Notification.Email.notConfigured.title',
                  'Invalid Email Address'
                ),
                l10n.t(
                  'LoginSso:Notification.Email.notConfigured.message',
                  `Your account is not set up for single sign on. Please contact your IT administrator for more information.`
                )
              )}
            </>
          )}

          {props.idpEnabled && (
            <>
              {renderDangerNotification(
                l10n.t(
                  'LoginSso:Notification.idpConfigured.title',
                  'Invalid Login Attempt'
                ),
                l10n.t(
                  'LoginSso:Notification.idpConfigured.message',
                  'To access your account,' +
                    ' please log in using your Single Sign-On (SSO).' +
                    ' If you encounter any issues or need further assistance,' +
                    ' please contact your SSO provider for support'
                )
              )}
            </>
          )}

          <EmailField
            className={styles.emailField}
            autoFocus={true}
            disabled={props.busy || props.alias !== ''}
            onChange={props.onEmailAddressChange}
            name="email"
            showError={props.showEmailError}
            value={props.email}
          />

          <div className={styles.divider}>
            <span data-testid="sso-or-divider">OR</span>
          </div>

          <SSOAliasField
            className={styles.ssoField}
            autoFocus={false}
            disabled={props.busy || props.email !== ''}
            onChange={props.onAliasChange}
            name="ssoalias"
            showError={props.showAliasError}
            value={props.alias}
          />

          <ActionButton
            disabled={props.alias === '' && props.email === ''}
            className={styles.actionButton}
            label={l10n.t('LoginSso:Button.continue', 'Continue')}
            loading={props.busy}
            type="submit"
          />

          <Footer className={styles.copyright} basic={true} />
        </form>
      </div>
    </div>
  );
}

export interface LoginSsoProps {
  /** Whether a request is in flight */
  busy: boolean;

  /** The current alias */
  alias: string;

  /** The current email address */
  email: string;

  /** Whether IDP is enabled */
  idpEnabled: boolean;

  /** Should we show the invalid email address error? */
  showAliasError: boolean;

  /** Should we show the invalid email address error? */
  showEmailError: boolean;

  /** Should we show the SSO error? */
  showSsoError: boolean;

  /** Back button handler */
  onBackButtonClick(event: React.SyntheticEvent<HTMLElement>): void;

  /** EmailField change handler */
  onAliasChange(alias: string, valid: boolean): void;

  /** EmailField change handler */
  onEmailAddressChange(email: string, valid: boolean): void;

  /** Submit handler */
  onSubmit(event: React.SyntheticEvent<HTMLElement>): void;
}
